import type { PayrollSummaryRow } from "../../api/types";

type WageRow = Pick<PayrollSummaryRow, "wageOwedEur" | "missingHourlyRate" | "unratedHours" | "shiftRatesNotPaidEur">;

/**
 * WO-B (OD-198): a payroll row's wage, honest about hours worked at no known
 * rate. The server counts those hours at €0 and says so
 * (`missingHourlyRate`, `unratedHours`); this cell never shows that €0 as if
 * it were the wage. Used by Finances (table and phone card) and the Analytics
 * staff table, each passing its own money formatter.
 */
export function PayrollWageCell({ row, format }: { row: WageRow; format: (eur: string) => string }) {
  return (
    <>
      <WageAmount row={row} format={format} />
      <ShiftRatesNotPaid row={row} format={format} />
    </>
  );
}

function WageAmount({ row, format }: { row: WageRow; format: (eur: string) => string }) {
  if (!row.missingHourlyRate) return <>{format(row.wageOwedEur)}</>;
  // No rated hour at all: there is no wage to show, only the gap.
  if (Number(row.wageOwedEur) === 0) {
    return <span className="badge warning">No hourly rate</span>;
  }
  return (
    <>
      {format(row.wageOwedEur)} <span className="payroll-unrated">+ {row.unratedHours} h with no rate</span>
    </>
  );
}

/**
 * G-163 (KI-107, OD-82): a salaried month pays its salary, never a wage; a rate
 * on one of its shifts is left out of the wage. The server states what it
 * would have paid; the cell shows it, so it is never silently dropped.
 */
function ShiftRatesNotPaid({ row, format }: { row: WageRow; format: (eur: string) => string }) {
  if (Number(row.shiftRatesNotPaidEur) <= 0) return null;
  return <span className="finances-counted-at muted">Salaried: {format(row.shiftRatesNotPaidEur)} at shift rates not paid</span>;
}

/** WO-B: the row's total leaves out the unrated hours — say so beside it. */
export function PayrollTotalNote({ row }: { row: Pick<PayrollSummaryRow, "missingHourlyRate"> }) {
  return row.missingHourlyRate ? <span className="payroll-unrated"> (rate missing)</span> : null;
}

/** WO-B: how many payroll rows leave hours out of their wage for want of a rate. */
export function countMissingRates(rows: Array<Pick<PayrollSummaryRow, "missingHourlyRate">>): number {
  return rows.filter((r) => r.missingHourlyRate).length;
}
